import React from 'react';
import { ApolloConsumer } from 'react-apollo';
import {
  AuthorisationConsumer,
  RequestProvider,
  UploaderConsumer,
  CreditsConsumer,
  GlobalStoreConsumer,
  withFlagsConsumer,
  hasPermission
} from '@shootsta/common-react';

import InvoicesContext, {
  InvoicesContextProvider
} from '../../../context/InvoicesContext';
import type { RouterHistory } from '../../../types';
import { SizeMe } from 'react-sizeme';
import {
  getFilteredVideosApi,
  getVideoCountCreatedByUserApi,
  deleteVideoApi,
  getOutputs,
  getUsersApi,
  createVideoApi,
  getFilteredEventsApi,
  createHistoryEntriesApi,
  getPaymentInvoiceApi,
  getVideoPaymentStatusApi
} from '../api';
import ProjectListComponent from '../components/ProjectList';

type ProjectListProps = {
  history: RouterHistory;
  headerTitle: string;
  headerSubtitle?: string;
  headerIcon?: string;
  headerIconColor?: string;
  headerBreadcrumbs?: any[];
  flags?: any;
  initialStatusFilter?: string[];
  lockStatusFilter?: boolean;
  tabs?: any;
};

function ProjectList(props: ProjectListProps) {
  const {
    flags: { workflowV5 = false }
  } = props;
  const tabs = {
    0: {
      searchTerm: '',
      sortBy: '',
      sortByLabel: '',
      selectedFilters: {},
      videos: []
    },
    1: {
      searchTerm: '',
      sortBy: '',
      sortByLabel: '',
      selectedFilters: {},
      videos: []
    },
    2: {
      searchTerm: '',
      sortBy: '',
      sortByLabel: '',
      selectedFilters: {},
      videos: []
    }
  } as const;

  return (
    <ApolloConsumer>
      {(apiClient) => (
        <AuthorisationConsumer requiresOrganisation>
          {({
            organisationMemberships,
            isShootstaUser,
            selectedOrganisation,
            isWorkspaceAdmin
          }) => {
            const {
              organisationId,
              permissions = [],
              package: subPackage,
              clientType
            } = selectedOrganisation || {};
            const canViewOwnedByEveryone =
              hasPermission('INTERNAL', permissions) || isWorkspaceAdmin;
            return (
              <RequestProvider
                requests={{
                  createVideo: createVideoApi(organisationId),
                  getFilteredVideos: getFilteredVideosApi(organisationId),
                  getVideoCountCreatedByUser: getVideoCountCreatedByUserApi(),
                  getOutputs: getOutputs(),
                  getUsers: getUsersApi(organisationId),
                  deleteVideo: deleteVideoApi,
                  getFilteredEvents: getFilteredEventsApi(),
                  createHistoryEntries: createHistoryEntriesApi(),
                  getPaymentInvoice: getPaymentInvoiceApi(),
                  getVideoPaymentStatus: getVideoPaymentStatusApi()
                }}
              >
                {(requests) => (
                  <CreditsConsumer>
                    {({ state: { canCreateOrSubmitProject } }) => (
                      <SizeMe>
                        {({ size }) => (
                          <UploaderConsumer>
                            {({ fetchAssets }) => (
                              <GlobalStoreConsumer>
                                {(globalStore) => (
                                  <InvoicesContextProvider
                                    getPaymentInvoice={
                                      requests.getPaymentInvoice
                                    }
                                  >
                                    <InvoicesContext.Consumer>
                                      {({ actions: { openInvoices } }) => (
                                        <ProjectListComponent
                                          selectedOrganisation={
                                            selectedOrganisation
                                          }
                                          isShootstaUser={isShootstaUser}
                                          apiClient={apiClient}
                                          containerSize={size}
                                          fetchAssets={fetchAssets}
                                          subPackage={subPackage}
                                          permissions={permissions}
                                          isAdmin={permissions.includes(
                                            'MANAGE_ORGANISATION'
                                          )}
                                          organisationId={organisationId}
                                          globalStore={globalStore}
                                          organisationMemberships={
                                            organisationMemberships
                                          }
                                          canCreateOrSubmitProject={
                                            canCreateOrSubmitProject
                                          }
                                          tabs={workflowV5 && tabs}
                                          openInvoices={openInvoices}
                                          canViewOwnedByEveryone={
                                            canViewOwnedByEveryone
                                          }
                                          clientType={clientType}
                                          {...requests}
                                          {...props}
                                        />
                                      )}
                                    </InvoicesContext.Consumer>
                                  </InvoicesContextProvider>
                                )}
                              </GlobalStoreConsumer>
                            )}
                          </UploaderConsumer>
                        )}
                      </SizeMe>
                    )}
                  </CreditsConsumer>
                )}
              </RequestProvider>
            );
          }}
        </AuthorisationConsumer>
      )}
    </ApolloConsumer>
  );
}

export default withFlagsConsumer(ProjectList);
